import { Wallet, X, Trash2, UserMinus } from "lucide-react";

// Welcome bet modal — shown once per (user, pool) the first time a member
// opens a pool that has bet details defined.
export function WelcomeBetModal({ pool, stakeLabel, onDismiss, t }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 pt-24 pb-8 overflow-y-auto" data-testid="welcome-bet-modal" onClick={onDismiss}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg border-2 border-[var(--accent-border)] bg-[var(--bg-card)] p-6 space-y-4">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[var(--accent-hex)] flex items-center justify-center">
                        <Wallet className="h-5 w-5 text-[var(--accent-text)]" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--accent-hex)]">{t("pool.bet.eyebrow")}</div>
                        <h2 className="font-heading text-xl font-extrabold uppercase text-[var(--text-primary)]">{t("pool.bet.title")}</h2>
                    </div>
                </div>
                <p className="text-sm text-[var(--text-secondary)]">{t("pool.bet.welcome")}</p>
                <div className="space-y-2 text-sm">
                    {stakeLabel && (
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("pool.bet.stake")}</div>
                            <div className="font-extrabold text-2xl text-[var(--accent-hex)]" data-testid="welcome-bet-stake">{stakeLabel}</div>
                        </div>
                    )}
                    {pool.bet_winner && (
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("pool.bet.winner")}</div>
                            <div className="text-[var(--text-primary)]" data-testid="welcome-bet-winner">{pool.bet_winner}</div>
                        </div>
                    )}
                    {pool.bet_instructions && (
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("pool.bet.instructions")}</div>
                            <div className="text-[var(--text-primary)] whitespace-pre-wrap" data-testid="welcome-bet-instructions">{pool.bet_instructions}</div>
                        </div>
                    )}
                </div>
                <button onClick={onDismiss} data-testid="welcome-bet-dismiss"
                    className="w-full bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-4 py-2.5 rounded-md hover:bg-[var(--accent-hex-hover)]">
                    {t("pool.bet.dismiss")}
                </button>
            </div>
        </div>
    );
}

// Admin edit bet modal — controlled by `betForm` from the parent.
export function BetEditModal({ betForm, setBetForm, onClose, onSubmit, saving, t }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 pt-24 pb-8 overflow-y-auto" data-testid="bet-edit-modal" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="w-full max-w-md rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-6 space-y-4 my-8">
                <div className="flex items-center justify-between">
                    <h2 className="font-heading text-xl font-extrabold uppercase text-[var(--text-primary)]">{t("pool.bet.title")}</h2>
                    <button type="button" onClick={onClose} data-testid="bet-edit-close" className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X className="h-5 w-5" /></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.modal.bet.amount")}</label>
                        <input value={betForm.bet_amount} onChange={(e) => setBetForm({ ...betForm, bet_amount: e.target.value })} type="number" inputMode="decimal" min="0" step="any" data-testid="bet-edit-amount"
                            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)]" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.modal.bet.currency")}</label>
                        <input value={betForm.bet_currency} onChange={(e) => setBetForm({ ...betForm, bet_currency: e.target.value.toUpperCase().slice(0, 6) })} data-testid="bet-edit-currency"
                            placeholder="SEK"
                            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md uppercase tracking-widest focus:outline-none focus:border-[var(--accent-hex)]" />
                    </div>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.modal.bet.winner")}</label>
                    <input value={betForm.bet_winner} onChange={(e) => setBetForm({ ...betForm, bet_winner: e.target.value })} data-testid="bet-edit-winner"
                        placeholder={t("dash.modal.bet.winner_ph")}
                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)]" />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("dash.modal.bet.instructions")}</label>
                    <textarea value={betForm.bet_instructions} onChange={(e) => setBetForm({ ...betForm, bet_instructions: e.target.value })} rows={3} data-testid="bet-edit-instructions"
                        placeholder={t("dash.modal.bet.instructions_ph")}
                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)]" />
                </div>
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={onClose} data-testid="bet-edit-cancel" className="flex-1 border border-[var(--border-hover)] text-[var(--text-primary)] font-bold uppercase tracking-wide px-4 py-2.5 rounded-md">{t("dash.modal.cancel")}</button>
                    <button type="submit" disabled={saving} data-testid="bet-edit-submit" className="flex-1 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-4 py-2.5 rounded-md hover:bg-[var(--accent-hex-hover)] disabled:opacity-50">
                        {saving ? "…" : t("pool.bet.save")}
                    </button>
                </div>
            </form>
        </div>
    );
}

export function DeletePoolModal({ poolName, deleting, onClose, onConfirm, t }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 pt-24 pb-8 overflow-y-auto" data-testid="delete-pool-modal" onClick={() => !deleting && onClose()}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg border border-red-500/40 bg-[var(--bg-card)] p-6 space-y-4">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
                        <Trash2 className="h-5 w-5 text-red-500" />
                    </div>
                    <div>
                        <h2 className="font-heading text-xl font-extrabold uppercase text-[var(--text-primary)]">{t("pool.admin.delete.title")}</h2>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">{t("pool.admin.delete.confirm", { name: poolName })}</p>
                        <p className="text-xs text-red-400 mt-2">{t("pool.admin.delete.warning")}</p>
                    </div>
                </div>
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={onClose} disabled={deleting} data-testid="delete-pool-cancel" className="flex-1 border border-[var(--border-hover)] text-[var(--text-primary)] font-bold uppercase tracking-wide px-4 py-2.5 rounded-md">{t("dash.modal.cancel")}</button>
                    <button type="button" onClick={onConfirm} disabled={deleting} data-testid="delete-pool-confirm"
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-extrabold uppercase tracking-wide px-4 py-2.5 rounded-md disabled:opacity-50">
                        {deleting ? "…" : t("pool.admin.delete.button")}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function RemoveMemberModal({ candidate, removing, onClose, onConfirm, t }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 pt-24 pb-8 overflow-y-auto" data-testid="remove-member-modal" onClick={() => !removing && onClose()}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg border border-red-500/40 bg-[var(--bg-card)] p-6 space-y-4">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
                        <UserMinus className="h-5 w-5 text-red-500" />
                    </div>
                    <div>
                        <h2 className="font-heading text-xl font-extrabold uppercase text-[var(--text-primary)]">{t("pool.members.remove.title")}</h2>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">{t("pool.members.remove.confirm", { name: candidate.name })}</p>
                        <p className="text-xs text-red-400 mt-2">{t("pool.members.remove.warning")}</p>
                    </div>
                </div>
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={onClose} disabled={removing} data-testid="remove-member-cancel" className="flex-1 border border-[var(--border-hover)] text-[var(--text-primary)] font-bold uppercase tracking-wide px-4 py-2.5 rounded-md">{t("dash.modal.cancel")}</button>
                    <button type="button" onClick={onConfirm} disabled={removing} data-testid="remove-member-confirm"
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-extrabold uppercase tracking-wide px-4 py-2.5 rounded-md disabled:opacity-50">
                        {removing ? "…" : t("pool.members.remove.button")}
                    </button>
                </div>
            </div>
        </div>
    );
}
